import { useEffect, useState, useRef } from 'react';
import { FaBuilding } from 'react-icons/fa6';
import moment from 'moment';
import { useNavigate, useParams } from 'react-router-dom';
import { ProgressCard } from 'components/apply-shared/progress-card';
import CancelApplicationModal from 'components/apply-shared/cancel-application-modal';
import { FaShieldAlt, FaCalendarAlt } from 'react-icons/fa';
import {
  ApplyAside,
  ApplyHeader,
  ApplyContainer,
  ApplyMain,
} from 'components/apply-shared/apply-form';
import { formatDateToHumanDate } from 'common/utils/formDateToHumanTime';
import { planStatusEnum } from 'common/utils/enums';
import {
  useGetSinglePlanQuery,
  useCreatePlanMutation,
} from 'redux/api/ciosUsersApi/ciosUsersApi';
import CreateQuote from 'components/plan-quote/create-quote';
import ShowQuote from 'components/plan-quote/show-quote';
import StartQuote from 'components/plan-quote/start-quote';
import { isEmpty } from 'common/utils/isEmpty';
import useUserTracker from 'common/utils/user-tracker/useUserTracker';
import DatesQuote from 'components/plan-quote/dates-quote';
import { useDispatch } from 'react-redux';
import {
  saveCoverageSelection,
  saveUpfrontSplitAmount,
} from 'redux/features/plans/plansSlice';
import { extractErrorMsg } from 'utils/errors';
import { toast } from 'react-toastify';

const QuoteSteps = {
  START: 'START',
  DATES: 'DATES',
  CREATE: 'CREATE',
  SHOW: 'SHOW',
};

const PlanQuote = () => {
  // HOOKS
  const navigate = useNavigate();
  const { planId } = useParams(); // Correctly extract planId from URL parameters
  const dispatch = useDispatch();
  const [createPlan, { isLoading: createPlanLoading }] =
    useCreatePlanMutation();
  const prevPlanIdRef = useRef();

  const {
    data: planData,
    isLoading: planLoading,
    error: planError,
    refetch: refetchPlan,
  } = useGetSinglePlanQuery(planId, {
    skip: !planId,
    refetchOnMountOrArgChange: true,
  });

  const {
    trackLink,
    trackPageLoad,
    trackFormSubmission,
    trackFormSubmissionError,
  } = useUserTracker();

  useEffect(() => {
    trackPageLoad('PlanQuote');
  }, []);

  // Clear coverage selection when the planId changes
  useEffect(() => {
    // If the planId exists and is different from the previously stored one,
    // it means we're loading a new plan. Reset selections.
    if (planId && planId !== prevPlanIdRef.current) {
      dispatch(saveCoverageSelection({}));
      dispatch(saveUpfrontSplitAmount(null));
    }
    // Update the ref for the next render.
    prevPlanIdRef.current = planId;
  }, [planId, dispatch]);

  // LOGIC REDIRECT
  // Make sure users with active, cancelled, or expired plans cannot access the apply page
  const activeStatuses = [
    planStatusEnum.ACTIVE,
    planStatusEnum.ON_GOING_CLAIM,
    planStatusEnum.COMPLETE,
    planStatusEnum.CLOSED,
  ];

  const reviewStatuses = [
    planStatusEnum.REVIEW,
    planStatusEnum.ACTIVATION_REQUIRED,
  ];

  const purchaseStatuses = [planStatusEnum.PENDING];

  const completeStatuses = [
    planStatusEnum.REJECTED,
    planStatusEnum.CANCELLED,
    planStatusEnum.SUSPENDED,
    planStatusEnum.EXPIRED,
  ];

  const notAllowedStatuses = [
    ...activeStatuses,
    ...completeStatuses,
    ...reviewStatuses,
    ...purchaseStatuses,
  ];

  useEffect(() => {
    if (notAllowedStatuses.includes(planData?.status)) {
      if (reviewStatuses.includes(planData?.status)) {
        navigate(`/dashboard/plans/activate/${planId}`);
      } else if (purchaseStatuses.includes(planData?.status)) {
        navigate(`/dashboard/plans/purchase/${planId}`);
      } else if (activeStatuses.includes(planData?.status)) {
        navigate(`/dashboard/plans/${planId}`);
      } else {
        navigate('/dashboard/plans');
      }
    }
  }, [planData, navigate, notAllowedStatuses, planId, activeStatuses]);

  // STATES
  const readyCriteria =
    (planData?.property || planData?.lease?.address) &&
    planData?.lease?.numBeds &&
    planData?.lease?.monthlyRent;
  const datesReady =
    !isEmpty(planData?.preLeaseExpiration) ||
    !isEmpty(planData?.lease?.signDate);
  const [cancelModalState, setCancelModalState] = useState(false);
  const [currentStep, setCurrentStep] = useState(QuoteSteps.START);

  useEffect(() => {
    // This effect synchronizes the view state with the data.
    // It should not run if the plan is ineligible, as the modal is handled
    // in the DatesQuote component.
    if (planData?.status === planStatusEnum.NOT_ELIGIBLE) {
      return;
    }
    
    // Don't change existing step numbers
    if (currentStep !== QuoteSteps.START) {
      return;
    }  

    if (readyCriteria) {
      setCurrentStep(QuoteSteps.SHOW);
    } else if (datesReady) {
      setCurrentStep(QuoteSteps.CREATE);
    } else if (planId) {
      setCurrentStep(QuoteSteps.DATES);
    } else {
      setCurrentStep(QuoteSteps.START);
    }
  }, [readyCriteria, datesReady, planData?.status, planId]);

  const getStepNumber = (step) => {
    if (step === QuoteSteps.START) {
      return 0;
    }
    if (step === QuoteSteps.DATES) return 1;
    if (step === QuoteSteps.CREATE) return 2;
    if (step === QuoteSteps.SHOW) return 3;
    return 1;
  };

  const handleStartQuote = () => {
    const submissionValues = { status: planStatusEnum.QUOTE };
    trackFormSubmission(submissionValues);
    createPlan(submissionValues)
      .unwrap()
      .then((data) => {
        // The create button already clears this, but good to have it here too for safety.
        dispatch(saveCoverageSelection({}));
        dispatch(saveUpfrontSplitAmount(null));
        navigate(`/dashboard/plans/quote/${data?.id}`);
      })
      .catch((error) => {
        const errMsg = extractErrorMsg(
          error,
          'We were unable to create your quote. Please try again.'
        );
        toast.error(errMsg);
        trackFormSubmissionError(submissionValues, errMsg);
      });
  };

  // HANDLERS
  const toggleCancelModal = () => {
    setCancelModalState(!cancelModalState);
  };

  // SIDE EFFECTS
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Calculate expiration date and days to expire
  const expirationDateString = planData?.expirationDate;
  let formattedExpirationDate = '';
  let daysToExpire = 0;
  if (expirationDateString) {
    formattedExpirationDate = formatDateToHumanDate(expirationDateString);
    // Compare in UTC to avoid timezone issues.
    daysToExpire = moment.utc(expirationDateString).diff(moment.utc(), 'days');
  }

  return (
    <>
      <ApplyContainer>
        {/* Header */}
        <ApplyHeader
          onClick={() => {
            navigate('/dashboard/plans?tab=quotes');
          }}
        />

        {/* Main Content */}
        <ApplyMain>
          {/* aside */}
          {![QuoteSteps.START, QuoteSteps.SHOW].includes(currentStep) && (
            <ApplyAside
              // Save text is different depending on whether the plan has been created, and whether the user is on the last step
              save={() => {
                navigate('/dashboard/plans?tab=quotes');
              }}
              statusText={expirationDateString}
              saveText={
                !isEmpty(planData) ? 'Save & Continue Later' : 'Cancel Quote'
              }
              // Cancel is only available once draftPlanID is set. It is also disabled after payment has been set (step 4)
              cancel={toggleCancelModal}
              cancelCondition={!isEmpty(planData)}
              cancelText='Cancel Quote'
            >
              <div
                className={`text-sm absolute inset-[0.5rem_0.5rem_0rem_0.5rem] flex flex-row justify-between items-start gap-2 lg:relative lg:inset-0 lg:px-3 lg:py-2 lg:border lg:border-gray-200 lg:rounded-md ${expirationDateString ? '' : 'lg:hidden'}`}
              >
                <span className='block lg:hidden mobile:whitespace-nowrap'>
                  Step {getStepNumber(currentStep)} of 3
                </span>
                {expirationDateString && (
                  <span className='text-gray-500 mobile:whitespace-nowrap'>
                    {moment
                      .utc(expirationDateString)
                      .isSame(moment.utc(), 'day') ? (
                      <>
                        Quote expires
                        <span className='font-semibold'> today</span>
                      </>
                    ) : (
                      <>
                        Quote expires in{' '}
                        <span className='font-semibold'>
                          {daysToExpire > 0 ? daysToExpire : 0} days
                        </span>{' '}
                        on{' '}
                        <span className='font-semibold'>
                          {formattedExpirationDate}
                        </span>
                      </>
                    )}
                  </span>
                )}
              </div>
              <ProgressCard
                CardIcon={FaCalendarAlt}
                title='Lease Dates'
                step={1}
                isBlurred={false}
                isCompleted={datesReady || currentStep !== QuoteSteps.DATES}
                onClick={() => setCurrentStep(QuoteSteps.DATES)}
                isCurrent={currentStep === QuoteSteps.DATES}
              />
              <ProgressCard
                CardIcon={FaBuilding}
                title='Rental Details'
                step={2}
                isBlurred={!datesReady}
                isCompleted={readyCriteria || currentStep === QuoteSteps.SHOW}
                onClick={() => {
                  if (datesReady) {
                    setCurrentStep(QuoteSteps.CREATE);
                  }
                }}
                isCurrent={currentStep === QuoteSteps.CREATE}
              />
              <ProgressCard
                CardIcon={FaShieldAlt}
                title='View Quote'
                step={3}
                isBlurred={!readyCriteria}
                isCompleted={planData?.status === planStatusEnum.PENDING}
                onClick={() => {
                  if (readyCriteria) {
                    setCurrentStep(QuoteSteps.SHOW);
                  }
                }}
                isCurrent={currentStep === QuoteSteps.SHOW}
              />
            </ApplyAside>
          )}

          <section className='h-full w-full flex flex-col items-center border border-gray-200 divide-y divide-gray-200 rounded-lg mobile:border-none mobile:rounded-none mobile:divide-gray-200'>
            {currentStep === QuoteSteps.START && (
              <StartQuote
                onNextClick={handleStartQuote}
                isLoading={createPlanLoading}
              />
            )}
            {currentStep === QuoteSteps.DATES && (
              <DatesQuote
                planId={planId}
                planData={planData}
                formikRef={null}
                onNextClick={async (createdPlanId) => {
                  if (planId === createdPlanId) {
                    await refetchPlan();
                  }
                  if (planId) {
                    navigate(`/dashboard/plans/quote/${planId}`);
                  } else if (createdPlanId) {
                    navigate(`/dashboard/plans/quote/${createdPlanId}`);
                  }
                  setCurrentStep(QuoteSteps.CREATE);
                }}
              />
            )}
            {currentStep === QuoteSteps.CREATE && (
              <CreateQuote
                planId={planId}
                planData={planData}
                formikRef={null}
                ready={readyCriteria}
                onBackClick={() => setCurrentStep(QuoteSteps.DATES)}
                onNextClick={async (createdPlanId) => {
                  // When updating an existing plan, the planId doesn't change.
                  // RTK Query will serve stale data from the cache unless we
                  // explicitly tell it to refetch.
                  if (planId === createdPlanId) {
                    // Awaiting the refetch ensures the new data is available
                    // before we try to render the <ShowQuote> component, preventing a flicker.
                    await refetchPlan();
                  }
                  if (planId) {
                    navigate(`/dashboard/plans/quote/${planId}`);
                  } else if (createdPlanId) {
                    // This is a new plan, so navigate to the new URL. The component
                    // will re-render and fetch the new data automatically.
                    navigate(`/dashboard/plans/quote/${createdPlanId}`);
                  }
                  setCurrentStep(QuoteSteps.SHOW);
                }}
              />
            )}
            {currentStep === QuoteSteps.SHOW && (
              <ShowQuote
                planId={planId}
                planData={planData}
                isLoading={planLoading}
                onBackClick={() => {
                  setCurrentStep(QuoteSteps.CREATE);
                }}
                onNextClick={() => {
                  navigate(`/dashboard/plans/purchase/${planId}`);
                }}
                onSaveClick={() => {
                  trackLink('Save Quote');
                  navigate('/dashboard/plans?tab=quotes');
                }}
              />
            )}
          </section>
        </ApplyMain>
      </ApplyContainer>

      {/* Cancel Modal */}
      {cancelModalState && (
        <CancelApplicationModal
          planId={planId}
          closeModal={toggleCancelModal}
        />
      )}
    </>
  );
};

export default PlanQuote;
